import { Children, MutableRefObject, ReactElement } from 'react';

import { assertUtil } from '@pukka/common/utils';

import { KeyType, OptionConfig, SelectOptionProps, SelectValue, SelectWithChildren, SelectWithOptions, ValueType } from './types';

function getValueAsText<T extends SelectValue>(value: T) {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }

  return value.value?.toString();
}

function getValueAsTextFrom<T extends SelectValue>(value?: T | T[], joiner = ', ') {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return undefined;
    }

    const result = value.map(getValueAsText).filter((v) => v !== null && v !== undefined);

    if (result.length === 0) {
      return undefined;
    }

    return result.join(joiner);
  }

  return getValueAsText(value);
}

function getValueAsTextFromReference<R extends OptionConfig<KeyType, ValueType, SelectValue>['reference']>(
  reference?: R | R[],
  joiner = ', ',
) {
  if (Array.isArray(reference)) {
    if (reference.length === 0) {
      return undefined;
    }

    const result = reference.map((r) => r.value).filter((r) => r !== undefined && r !== null);

    if (result.length === 0) {
      return undefined;
    }

    return result.join(joiner);
  }

  return reference?.value?.toString();
}

function getValueAsTextFromOption<K extends KeyType, V extends ValueType, T extends SelectValue<K, V>>(
  option?: OptionConfig<K, V, T> | (OptionConfig<K, V, T> | undefined)[],
  joiner = ', ',
) {
  if (Array.isArray(option)) {
    if (option.length === 0) {
      return undefined;
    }

    const result = option.map((o) => o?.reference.value).filter((o) => o !== undefined && o !== null);

    if (result.length === 0) {
      return undefined;
    }

    return result.join(joiner);
  }

  return option?.reference.value?.toString();
}

function isKeyType(key?: unknown) {
  if (typeof key === 'string' || typeof key === 'number' || typeof key === 'bigint' || typeof key === 'boolean') {
    return true;
  }

  return false;
}

function getKeyFrom<K extends KeyType, V extends ValueType, T extends SelectValue<K, V>>(
  value?: T | T[],
): K | V | undefined | (K | V | undefined)[] {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return undefined;
    }

    return value.map((v) => getKeyFrom(v as K)) as V[];
  }

  if (isKeyType(value)) {
    return value as K;
  }

  assertUtil.assertObjectIsTruthy<Exclude<T, K>>(value);

  return value.key ?? value.value;
}

function getKeyValuePair<T extends SelectValue>(val?: T) {
  const value = (val as Exclude<T, KeyType>)?.value ?? val;
  const key = (val as Exclude<T, KeyType>)?.key ?? value;

  return {
    key,
    value,
    isKeyType: isKeyType(key),
  };
}

function getOriginalValueFromOption<K extends KeyType, V extends ValueType, T extends SelectValue<K, V>>(
  option: OptionConfig<K, V, T> | undefined,
) {
  return option?.value;
}

function getOption<K extends KeyType, V extends ValueType, T extends SelectValue<K, V>>(
  key: V,
  map: Map<KeyType, OptionConfig<K, V, T>>,
  weakMap: WeakMap<object, OptionConfig<K, V, T>>,
) {
  if (key === null || key === undefined) {
    return undefined;
  }

  if (isKeyType(key)) {
    assertUtil.assertIsNotUndefinedNorNull<K>(key);

    return map.get(key);
  }

  assertUtil.assertIsNotUndefinedNorNull<Exclude<typeof key, K>>(key);

  return weakMap.get(key);
}

function getOptionConfigWith<K extends KeyType, V extends ValueType, T extends SelectValue<K, V>>(
  key: V | T | SelectOptionProps<T> | undefined | null,
  value: V | T | SelectOptionProps<T>,
  valueObj: T,
  map: Map<KeyType, OptionConfig<K, V, T>>,
  weakMap: WeakMap<object, OptionConfig<K, V, T>>,
  props?: T | SelectOptionProps<T>,
  element?: ReactElement,
): OptionConfig<K, V, T> | null {
  if (key === null || key === undefined) {
    return null;
  }

  const propsObject = isKeyType(props) ? undefined : (props as Exclude<typeof props, K>);

  if (isKeyType(key)) {
    assertUtil.assertIsNotUndefinedNorNull<K>(key);

    if (!map.has(key)) {
      map.set(key, {
        props: propsObject,
        element,
        value: valueObj,
        reference: {
          key: key as V,
          value: value as V,
        },
      });
    }

    return map.get(key) as OptionConfig<K, V, T>;
  }

  assertUtil.assertIsNotUndefinedNorNull<Exclude<typeof key, K>>(key);

  if (!weakMap.has(key)) {
    weakMap.set(key, {
      props: propsObject,
      element,
      value: valueObj,
      reference: {
        key: key as V,
        value: value as V,
      },
    });
  }

  return weakMap.get(key) as OptionConfig<K, V, T>;
}

function getOptionConfigs<K extends KeyType, V extends ValueType, T extends SelectValue<K, V>>(
  map: MutableRefObject<Map<KeyType, OptionConfig<K, V, T>>>,
  weakMap: MutableRefObject<WeakMap<object, OptionConfig<K, V, T>>>,
  options?: SelectWithOptions<K, V>['options'],
  children?: SelectWithChildren<T>['children'],
): (OptionConfig<K, V, T> | null)[] {
  if (options) {
    // Change every K to SelectOptionProps
    return options.map((option) => {
      const value =
        ((option as SelectOptionProps<T> | undefined)?.value as Exclude<T, K> | undefined)?.value ??
        ((option as SelectOptionProps<T> | undefined)?.value as V) ??
        ((option as SelectOptionProps<T> | undefined)?.children as V) ??
        option;
      const key =
        ((option as SelectOptionProps<T> | undefined)?.value as Exclude<T, K> | undefined)?.key ??
        ((option as Exclude<T, K> | undefined)?.key as K) ??
        value;
      const keys = Object.keys(option);
      let valueObj: T;

      if (isKeyType(option)) {
        valueObj = option as T;
      } else if (!isKeyType((option as SelectOptionProps<T> | undefined)?.value)) {
        valueObj = (option as SelectOptionProps<T>).value as T;
      } else if ((keys.length === 1 && keys[0] === 'value') || (option as Exclude<T, K>).key !== undefined) {
        valueObj = option as T;
      } else {
        valueObj = (option as SelectOptionProps<T>).value as T;
      }

      return getOptionConfigWith(key, value, valueObj, map.current, weakMap.current, option) as OptionConfig<K, V, T>;
    });
  }

  if (children) {
    return Children.map(children, (child) => {
      if (!child) {
        return null;
      }

      const { value: valueProps, ...props } = child.props;

      if (typeof props.children !== 'object') {
        const value = (props.children as V) ?? (valueProps as Exclude<T, K> | undefined)?.value ?? (valueProps as V);
        const key =
          (valueProps as Exclude<T, K> | undefined)?.key ?? (valueProps as Exclude<T, K> | undefined)?.value ?? (valueProps as V) ?? value;

        return getOptionConfigWith(key as V, value, (valueProps ?? props.children) as T, map.current, weakMap.current, child.props, child);
      }

      const value = (valueProps as Exclude<T, K> | undefined)?.value ?? (valueProps as V) ?? (props.children as V);
      const key = (valueProps as Exclude<T, K> | undefined)?.key ?? value;

      return getOptionConfigWith(key as V, value, (valueProps ?? props.children) as T, map.current, weakMap.current, child.props, child);
    });
  }

  return [];
}

function getStateDefaultValue<K extends KeyType, V extends ValueType, T extends SelectValue<K, V>>(
  map: Map<KeyType, OptionConfig<K, V, T>>,
  weakMap: WeakMap<object, OptionConfig<K, V, T>>,
  value?: T | T[],
) {
  const stateValues = ([] as T[])
    .concat(value ?? [])
    .map((v) => {
      const { key, isKeyType: isKey } = getKeyValuePair(v);

      if (isKey) {
        return map.get(key as K)?.reference;
      }

      return weakMap.get(key as Exclude<T, K>)?.reference;
    })
    .filter((v) => !!v) as OptionConfig<K, V, T>['reference'][];

  return Array.isArray(value) ? stateValues : stateValues.pop();
}

export {
  getValueAsTextFrom,
  getOptionConfigs,
  getStateDefaultValue,
  getOption,
  getOriginalValueFromOption,
  getValueAsTextFromOption,
  getValueAsTextFromReference,
  getKeyFrom,
  getOptionConfigWith as _getOptionConfigWith,
};
